import type { IconName } from '../constants/iconNames';
import { DEFAULT_DOCUMENT_FORMATS, documentOpensIn, type FileFormat } from './fileFormats';

export interface LinkedFile {
  id: string;
  originalName: string;
  type: string;
  extension: string;
  status: string;
}

export const LINKED_FILE_GROUPS: { type: string; label: string; icon: IconName }[] = [
  { type: 'document',    label: 'Documents',       icon: 'description' },
  { type: 'two_d',       label: '2D Drawings',     icon: 'architecture' },
  { type: 'three_d',     label: '3D BIM Models',   icon: 'view_in_ar' },
  { type: 'point_cloud', label: 'Point Clouds',    icon: 'grain' },
  { type: 'gaussian',    label: 'Gaussian Splats', icon: 'blur_on' },
  { type: 'orthomosaic', label: 'Orthomosaics',    icon: 'satellite_alt' },
  { type: 'video360',    label: '360 Videos',      icon: 'panorama_photosphere' },
];

export const linkedFileIcon = (type: string): IconName =>
  LINKED_FILE_GROUPS.find((g) => g.type === type)?.icon ?? 'insert_drive_file';

const OWN_VIEWER: Record<string, string> = {
  two_d: 'viewer-2d', three_d: 'viewer-3d-v2', document: 'viewer-pdf',
  point_cloud: 'scene', gaussian: 'viewer-splat', orthomosaic: 'viewer-ortho', video360: 'viewer-360',
};
const SHARE_VIEWER: Record<string, string> = {
  two_d: 'viewer-2d', three_d: 'viewer-3d', document: 'viewer-pdf',
  point_cloud: 'scene', gaussian: 'viewer-splat', orthomosaic: 'viewer-ortho', video360: 'viewer-360',
};

// A document opens in the Image Viewer when File Formats say so; every other
// one goes to the PDF viewer, which shows a download-only format as a Download panel.
const viewerFor = (file: LinkedFile, table: Record<string, string>, formats: FileFormat[]): string | undefined =>
  file.type === 'document' && documentOpensIn(file.extension, formats) === 'image' ? 'viewer-image' : table[file.type];

// Where a linked file opens when the viewer is not inside the Scene (which
// opens it in its split pane instead).
export function linkedFileRoute(
  file: LinkedFile, projectId: string | undefined, shareToken: string | undefined, formats: FileFormat[] = DEFAULT_DOCUMENT_FORMATS,
): string | null {
  if (shareToken) {
    const v = viewerFor(file, SHARE_VIEWER, formats);
    if (!v) return null;
    return v === 'scene' ? `/share/${shareToken}/scene` : `/share/${shareToken}/${v}/${file.id}`;
  }
  const v = viewerFor(file, OWN_VIEWER, formats);
  if (!v || !projectId) return null;
  return v === 'scene' ? `/projects/${projectId}/scene` : `/projects/${projectId}/${v}/${file.id}`;
}
